'use client';

import { useEffect, useRef, useState } from 'react';
import { toImageItems } from '@/lib/media';
import { SmartImg } from './Skeleton';
import Lightbox from './Lightbox';

interface DragGalleryProps {
  images: string[];
  label: string;
}

const SPEED = 50; // px per second
const GAP = 24; // matches gap-6 (24px)

/**
 * Continuously auto-scrolling photo strip that is also drag/swipe-navigable.
 *
 * Architecture (so auto-scroll and interaction never fight over `transform`):
 * - The infinite marquee is a Web Animations API animation on the TRACK — it is
 *   the only thing that ever writes `transform`.
 * - Drag (mouse) and swipe (touch) do NOT write transform; they only scrub the
 *   animation's `currentTime`. On release the animation simply plays on from the
 *   scrubbed position, so it keeps looping continuously — no snap-back, no jump.
 * - A press pauses the marquee for the duration of the gesture, so a clean tap
 *   lands on a stationary tile and opens exactly that image. The `moved` flag
 *   suppresses the click that ends a drag.
 */
export default function DragGallery({ images, label }: DragGalleryProps) {
  const [open, setOpen] = useState(false);
  const [index, setIndex] = useState(0);

  const trackRef = useRef<HTMLDivElement>(null);
  const animRef = useRef<Animation | null>(null);
  const periodRef = useRef(0);
  const durationRef = useRef(0);
  const reduceRef = useRef(false);
  const drag = useRef({ active: false, startX: 0, startTime: 0, moved: false });

  const loop = [...images, ...images];

  // Build (and rebuild on resize) the infinite marquee animation.
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;

    reduceRef.current = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    function build() {
      if (!track) return;
      animRef.current?.cancel();
      // One repeat period = half the track (images are duplicated), plus the gap
      // that sits between the two sets.
      const period = (track.scrollWidth + GAP) / 2;
      if (period <= 0) return;
      const duration = (period / SPEED) * 1000;
      periodRef.current = period;
      durationRef.current = duration;

      const anim = track.animate(
        [{ transform: 'translateX(0)' }, { transform: `translateX(${-period}px)` }],
        { duration, iterations: Infinity, easing: 'linear' }
      );
      if (reduceRef.current) anim.pause(); // reduced motion: static, but still drag-scrubbable
      animRef.current = anim;
    }

    build();
    window.addEventListener('resize', build);
    return () => {
      window.removeEventListener('resize', build);
      animRef.current?.cancel();
    };
  }, [images.length]);

  function pause() {
    animRef.current?.pause();
  }
  function play() {
    if (!drag.current.active && !reduceRef.current) animRef.current?.play();
  }

  function scrub(dx: number) {
    const anim = animRef.current;
    const dur = durationRef.current;
    const period = periodRef.current;
    if (!anim || !dur || !period) return;
    // Dragging right (dx > 0) reveals earlier content → wind the timeline back.
    let t = drag.current.startTime - (dx / period) * dur;
    t = ((t % dur) + dur) % dur;
    anim.currentTime = t;
  }

  function startDrag(clientX: number) {
    pause();
    drag.current = {
      active: true,
      startX: clientX,
      startTime: Number(animRef.current?.currentTime ?? 0),
      moved: false,
    };
  }
  function moveDrag(clientX: number) {
    if (!drag.current.active) return;
    const dx = clientX - drag.current.startX;
    if (Math.abs(dx) > 6) drag.current.moved = true;
    scrub(dx);
  }
  function endDrag() {
    if (!drag.current.active) return;
    drag.current.active = false;
    play();
  }

  function openAt(i: number) {
    if (drag.current.moved) return; // it was a drag, not a click
    setIndex(i % images.length);
    setOpen(true);
  }

  return (
    <>
      <div
        className="marquee-mask relative w-full max-w-full cursor-grab touch-pan-y select-none overflow-hidden active:cursor-grabbing"
        onMouseDown={(e) => startDrag(e.pageX)}
        onMouseMove={(e) => moveDrag(e.pageX)}
        onMouseUp={endDrag}
        onMouseLeave={endDrag}
        onTouchStart={(e) => startDrag(e.touches[0].pageX)}
        onTouchMove={(e) => moveDrag(e.touches[0].pageX)}
        onTouchEnd={endDrag}
      >
        <div ref={trackRef} className="flex w-max gap-6 will-change-transform">
          {loop.map((src, i) => (
            <button
              key={`${src}-${i}`}
              type="button"
              onClick={() => openAt(i)}
              aria-label={`${label} ${(i % images.length) + 1} — open`}
              className="forest-img-shadow relative h-48 w-48 flex-shrink-0 select-none overflow-hidden rounded-sm border border-surface-bright/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary md:h-64 md:w-64"
            >
              <SmartImg
                src={encodeURI(src)}
                alt={`${label} ${(i % images.length) + 1}`}
                loading="lazy"
                draggable={false}
                className="pointer-events-none h-full w-full select-none object-cover object-center"
              />
            </button>
          ))}
        </div>
      </div>

      <Lightbox
        items={toImageItems(images)}
        index={index}
        open={open}
        label={label}
        onClose={() => setOpen(false)}
        onIndexChange={setIndex}
      />
    </>
  );
}
